import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CheckPhotoSlot, InlineDeleteButton } from "@/components/check-shared";
import { IconLock, IconLockOpen } from "@tabler/icons-react";
import { timeAgo } from "@/lib/time";
import { asToeCheckId, type ToeCheckId } from "@/types/identifiers";
import type { ToeCheckActions } from "@/hooks/use-toe-check-actions";
import type { Action } from "@/providers/auth-provider";
import type { components } from "@/api/generated/schema";

type ToeCheck = components["schemas"]["ToeCheckResponse"];

interface ToeCheckCardProps {
  check: ToeCheck;
  actions: ToeCheckActions;
  can: (action: Action) => boolean;
  isUploading?: boolean;
  onPhotoClick: () => void;
  readOnly?: boolean;
}

export function ToeCheckCard({ check, actions, can, isUploading, onPhotoClick, readOnly = false }: ToeCheckCardProps) {
  const checkId = asToeCheckId(check.id) as ToeCheckId;
  const isSigned = !!check.signed_off_at;
  const canEdit = !readOnly;

  const borderColor = isSigned ? "border-l-green-500" : "border-l-blue-400";

  return (
    <div className={`border-l-3 pl-5 pr-5 py-4 flex gap-4 rounded-r-lg bg-card ${borderColor}`}>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-muted-foreground">
          Checked by <span className="font-medium text-foreground">{check.checked_by}</span>
          <span className="text-muted-foreground/50 ml-1.5">{timeAgo(check.checked_at)}</span>
        </div>

        {isSigned && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-[11px] font-medium text-green-600 dark:text-green-400 flex items-center gap-1">
              <IconLock className="h-3 w-3" />
              Signed off by {check.signed_off_by}
            </span>
            {canEdit && can("manage-calibrations") && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => actions.handleUnlock(checkId)}
                    disabled={actions.isUnlocking}
                  >
                    <IconLockOpen className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Unlock</TooltipContent>
              </Tooltip>
            )}
          </div>
        )}

        {!isSigned && canEdit && (
          <div className="mt-2 flex items-center gap-2">
            {can("sign-off") && (
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-3 text-xs"
                onClick={() => actions.handleSignOff(checkId)}
                disabled={actions.isSigningOff}
              >
                <IconLock className="mr-1 h-3 w-3" />
                Sign off
              </Button>
            )}
            {can("manage-calibrations") && (
              <InlineDeleteButton
                onConfirm={() => actions.handleDelete(checkId)}
                disabled={actions.isDeleting}
              />
            )}
          </div>
        )}
      </div>

      <CheckPhotoSlot
        thumbnailUrl={check.photo?.thumbnail_url}
        alt="Toe check photo"
        isUploading={isUploading}
        canEdit={canEdit}
        onPhotoClick={onPhotoClick}
      />
    </div>
  );
}
